import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Copy, Wallet } from 'lucide-react';
import { useAccount, useDisconnect } from 'wagmi';
import { useWallet } from '@solana/wallet-adapter-react';
import { useAppContext } from '../contexts/hooks';
import { clearAllKeyPairs } from '../services/auth';
import { trackEvent } from '../lib/analytics';
import { cn } from '@/lib/utils';
import { OrderlyLogo } from './orderly/OrderlyLogo';

const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true';
const ABSTRACT_CONNECTOR_ID = 'xyz.abs.privy';
const ABSTRACT_ICON_URL = 'https://abstract-assets.abs.xyz/icons/light.png';

function formatAddress(address: string) {
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

type WalletDisplay = {
  id: string;
  label: string;
  address: string;
  iconUrl?: string;
  chain?: 'evm' | 'solana';
};

type IconSource = string | (() => Promise<string>);

type ConnectorWithIcon = {
  icon?: string;
  iconUrl?: IconSource;
  rkDetails?: {
    iconUrl?: IconSource;
  };
};

function getConnectorIconSource(connector: unknown): IconSource | undefined {
  const connectorWithIcon = connector as ConnectorWithIcon | undefined;
  return connectorWithIcon?.icon ?? connectorWithIcon?.iconUrl ?? connectorWithIcon?.rkDetails?.iconUrl;
}

export default function Header() {
  const { state, dispatch } = useAppContext();
  const { address: wagmiAddress, connector } = useAccount();
  const { disconnect } = useDisconnect();
  const { disconnect: disconnectSolana, publicKey: solanaPublicKey, wallet: solanaWallet } = useWallet();
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [copiedWalletId, setCopiedWalletId] = useState<string | null>(null);
  const [resolvedEvmIcon, setResolvedEvmIcon] = useState<{ source?: IconSource; url?: string }>({});
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const copyResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectedEntries = Object.entries(state.connectedWallets).filter(([, address]) => Boolean(address)) as Array<[
    'evm' | 'solana',
    string,
  ]>;
  const activeChain: 'evm' | 'solana' | null = state.chain ?? connectedEntries[0]?.[0] ?? null;
  const isAbstractWallet = connector?.id === ABSTRACT_CONNECTOR_ID || connector?.name === 'Abstract';
  const evmAddress = state.connectedWallets.evm ?? null;
  const solanaAddress = state.connectedWallets.solana ?? solanaPublicKey?.toBase58() ?? null;
  const abstractAddress = isAbstractWallet ? wagmiAddress ?? evmAddress : null;
  const evmIconSource = isAbstractWallet ? ABSTRACT_ICON_URL : getConnectorIconSource(connector);
  const evmIconUrl = typeof evmIconSource === 'string'
    ? evmIconSource
    : resolvedEvmIcon.source === evmIconSource
      ? resolvedEvmIcon.url
      : undefined;
  const solanaIconUrl = solanaWallet?.adapter.icon;
  const activeWallet: WalletDisplay | null = activeChain === 'solana' && solanaAddress
    ? { id: 'solana', label: 'Solana', address: solanaAddress, iconUrl: solanaIconUrl, chain: 'solana' }
    : activeChain === 'evm' && isAbstractWallet && abstractAddress
      ? { id: 'abstract', label: 'Abstract', address: abstractAddress, iconUrl: ABSTRACT_ICON_URL }
      : activeChain === 'evm' && evmAddress
        ? { id: 'evm', label: 'EVM', address: evmAddress, iconUrl: evmIconUrl, chain: 'evm' }
        : state.walletAddress
          ? { id: 'wallet', label: 'Wallet', address: state.walletAddress }
          : null;
  const dropdownWallets: WalletDisplay[] = [];

  if (activeWallet) {
    dropdownWallets.push(activeWallet);
  }

  if (activeChain === 'solana' && evmAddress) {
    dropdownWallets.push({ id: 'evm', label: 'EVM', address: evmAddress, iconUrl: evmIconUrl, chain: 'evm' });
  }

  if (activeChain !== 'solana' && solanaAddress) {
    dropdownWallets.push({ id: 'solana', label: 'Solana', address: solanaAddress, iconUrl: solanaIconUrl, chain: 'solana' });
  }

  useEffect(() => {
    let cancelled = false;

    if (typeof evmIconSource !== 'function') {
      return;
    }

    evmIconSource()
      .then((iconUrl) => {
        if (!cancelled) setResolvedEvmIcon({ source: evmIconSource, url: iconUrl });
      })
      .catch(() => {
        if (!cancelled) setResolvedEvmIcon({ source: evmIconSource, url: undefined });
      });

    return () => {
      cancelled = true;
    };
  }, [evmIconSource]);

  useEffect(() => {
    trackEvent('component_viewed', {
      component: 'Header',
      page: state.page,
      chain: state.chain ?? undefined,
      status: state.walletAddress ? 'connected' : 'disconnected',
    });
  }, [state.page, state.chain, state.walletAddress]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) {
        setAccountMenuOpen(false);
      }
    }

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current) clearTimeout(copyResetTimeoutRef.current);
    };
  }, []);

  async function handleCopyAddress(wallet: WalletDisplay) {
    await navigator.clipboard.writeText(wallet.address);
    setCopiedWalletId(wallet.id);
    if (copyResetTimeoutRef.current) clearTimeout(copyResetTimeoutRef.current);
    copyResetTimeoutRef.current = setTimeout(() => setCopiedWalletId(null), 1500);
  }

  async function handleDisconnect() {
    clearAllKeyPairs();
    if (!USE_MOCK) {
      if (state.connectedWallets.solana) {
        await disconnectSolana();
      }
      if (state.connectedWallets.evm) {
        disconnect();
      }
    }
    dispatch({ type: 'DISCONNECT_WALLET' });
  }

  return (
    <header className="sticky top-0 z-20 border-b border-[var(--border-subtle)] bg-[var(--bg-base)]/80 backdrop-blur-[12px]" aria-label="App header">
      <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between gap-4 px-4 md:px-8">
        <div className="flex min-w-0 items-center gap-2.5 text-[var(--fg-base)]">
          <OrderlyLogo className="h-8 w-8 shrink-0" />
          <div className="min-w-0">
            <div className="font-display text-base font-medium leading-[120%] text-[var(--fg-base)]">Orderly</div>
            <div className="mt-0.5 hidden text-xs font-medium leading-[120%] text-[var(--fg-secondary)] sm:block">Emergency Exit</div>
          </div>
        </div>
        <div className="flex min-w-0 items-center justify-end">
          {state.walletAddress && activeWallet && (
            <div className="relative" ref={accountMenuRef}>
              <button
                type="button"
                className="flex h-10 min-w-0 max-w-[min(280px,calc(100vw-152px))] items-center gap-2.5 rounded-[24px] bg-[var(--bg-secondary)] px-3 text-[var(--fg-base)] transition-opacity hover:opacity-90"
                onClick={() => setAccountMenuOpen((open) => !open)}
                aria-haspopup="true"
                aria-expanded={accountMenuOpen}
              >
                <span className="grid h-7 w-7 shrink-0 place-items-center overflow-hidden rounded-full text-[var(--fg-base)]">
                  {activeWallet.iconUrl ? (
                    <img className="h-6 w-6 rounded-full" src={activeWallet.iconUrl} alt="" aria-hidden="true" />
                  ) : (
                    <Wallet className="h-4 w-4" aria-hidden="true" />
                  )}
                </span>
                <span className="min-w-0 flex-1 text-left">
                  <span className="block truncate text-sm font-medium leading-[120%] text-[var(--fg-base)]">{activeWallet.label}</span>
                  <span className="block truncate font-mono text-xs leading-[120%] text-[var(--fg-secondary)]">{formatAddress(activeWallet.address)}</span>
                </span>
                <ChevronDown
                  className={cn(
                    'h-3.5 w-3.5 shrink-0 text-[var(--fg-secondary)] transition-transform',
                    accountMenuOpen && 'rotate-180'
                  )}
                />
              </button>

              {accountMenuOpen && (
                <div className="absolute right-0 top-[calc(100%+8px)] z-50 flex w-[min(420px,calc(100vw-24px))] flex-col gap-1.5 rounded-lg bg-[var(--bg-surface-primary)] p-4 shadow-[var(--shadow-popout)]">
                  {dropdownWallets.map((wallet) => {
                    const isActive = wallet.id === activeWallet.id;
                    return (
                      <div
                        key={wallet.id}
                        className={cn(
                          'flex min-h-[51px] flex-col gap-1 rounded-lg px-3 py-2 text-left',
                          isActive ? 'text-[var(--fg-base)]' : 'text-[var(--fg-secondary)]'
                        )}
                      >
                        <span className="text-sm font-medium">{wallet.label}</span>
                        <span className="flex max-w-full flex-wrap items-center gap-1.5">
                          <span className="min-w-0 break-all font-mono text-xs leading-5 text-[var(--fg-secondary)]">{wallet.address}</span>
                          <button
                            type="button"
                            className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-[var(--fg-secondary)] transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--fg-base)]"
                            onClick={() => void handleCopyAddress(wallet)}
                            aria-label={`Copy ${wallet.label} address`}
                          >
                            {copiedWalletId === wallet.id ? (
                              <Check className="h-3.5 w-3.5" aria-hidden="true" />
                            ) : (
                              <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                            )}
                          </button>
                        </span>
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    className="flex min-h-[51px] items-center rounded-lg px-3 py-2 text-left text-sm font-medium text-[var(--fg-negative)] transition-colors hover:bg-[var(--bg-hover)]"
                    onClick={() => {
                      setAccountMenuOpen(false);
                      void handleDisconnect();
                    }}
                  >
                    Disconnect
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
